// =====================================================================
// ErrorsList.jsx — Liste d'erreurs (filtres + table)
// Mode 'mine' (operateur, propres) sau 'all' (admin)
// =====================================================================
import { useState, useEffect, useMemo } from 'react';
import { errors as errorsApi } from '../../services/api.js';
import { marqueurInactif } from '../../lib/tri-personnes.js';

function formatDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

const RESP_BADGE = {
  YES: { label: 'Oui', bg: '#fee2e2', color: '#b91c1c' },
  NO:  { label: 'Non', bg: '#dcfce7', color: '#166534' },
  TBD: { label: 'À déterminer', bg: '#fef3c7', color: '#92400e' },
};

const CATEGORY_COLOR = {
  PREPARATION: { bg: '#fce7f3', color: '#9d174d' },
  ENCODAGE: { bg: '#dbeafe', color: '#1e40af' },
  PROCEDURE: { bg: '#fed7aa', color: '#9a3412' },
  RECEPTION: { bg: '#d1fae5', color: '#065f46' },
  TRI: { bg: '#e9d5ff', color: '#5b21b6' },
  INVENTAIRE: { bg: '#fef3c7', color: '#854d0e' },
  ADMIN: { bg: '#fef9c3', color: '#713f12' },
  SECURITE: { bg: '#fee2e2', color: '#b91c1c' },
  REGLEMENT: { bg: '#cffafe', color: '#155e75' },
};

export default function ErrorsList({ mode, typologies, users, projects, canEdit, canDelete, onEdit, onCreate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [filterOp, setFilterOp] = useState('');
  const [filterTyp, setFilterTyp] = useState('');
  const [filterProj, setFilterProj] = useState('');
  const [filterResp, setFilterResp] = useState('');

  async function load() {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (mode === 'all') {
        if (filterOp) params.operatorUserId = filterOp;
        if (filterTyp) params.typologyId = filterTyp;
        if (filterProj) params.projectId = filterProj;
        if (filterResp) params.brainResponsibility = filterResp;
      } else {
        if (filterTyp) params.typologyId = filterTyp;
      }
      const data = mode === 'all' ? await errorsApi.listAll(params) : await errorsApi.listMe(params);
      setItems(data);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [mode, from, to, filterOp, filterTyp, filterProj, filterResp]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.movementRef || '').toLowerCase().includes(q) ||
      (it.operator?.name || '').toLowerCase().includes(q) ||
      (it.project?.name || '').toLowerCase().includes(q) ||
      (it.explanation || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalCost = filtered.reduce((s, it) => s + (it.cost || 0), 0);

  async function handleDelete(it) {
    if (!confirm(`Supprimer cette erreur ?\n\n${it.operator?.name || ''} · ${it.typology?.label || ''}\n${it.date}`)) return;
    try {
      await errorsApi.delete(it.id);
      load();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.message || e.message));
    }
  }

  return (
    <div>
      {/* Banner pour Mes erreurs */}
      {mode === 'mine' && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#075985' }}>
          💡 <b>Mes erreurs</b> — Voici les retours que tu as reçus. Discute-les avec ton chef d'équipe pour t'améliorer.
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input type="text" placeholder="🔍 Recherche réf, opérateur, client, comm..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: 220, maxWidth: 380 }} />
        {mode === 'all' && users && (
          <select value={filterOp} onChange={e => setFilterOp(e.target.value)} style={inputStyle}>
            <option value="">Tous opérateurs</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}{marqueurInactif(u)}</option>)}
          </select>
        )}
        <select value={filterTyp} onChange={e => setFilterTyp(e.target.value)} style={inputStyle}>
          <option value="">Toutes typologies</option>
          {typologies.map(t => <option key={t.id} value={t.id}>{t.label.slice(0, 50)}</option>)}
        </select>
        {mode === 'all' && projects && (
          <select value={filterProj} onChange={e => setFilterProj(e.target.value)} style={inputStyle}>
            <option value="">Tous clients</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.isActive === false ? ' (archivé)' : ''}</option>)}
          </select>
        )}
        {mode === 'all' && (
          <select value={filterResp} onChange={e => setFilterResp(e.target.value)} style={inputStyle}>
            <option value="">Toute resp.</option>
            <option value="YES">Resp. BEL = Oui</option>
            <option value="NO">Resp. BEL = Non</option>
            <option value="TBD">À déterminer</option>
          </select>
        )}
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, width: 140 }} placeholder="De" />
        <span style={{ color: '#94a3b8' }}>→</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, width: 140 }} placeholder="À" />
        {mode === 'all' && onCreate && (
          <button onClick={onCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>+ Nouvelle erreur</button>
        )}
      </div>

      {loading && <div style={{ padding: 24, color: '#64748b' }}>Chargement…</div>}
      {err && <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 6 }}>Erreur: {err}</div>}
      {!loading && !err && filtered.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 8 }}>
          {mode === 'mine' ? 'Aucune erreur à ton nom 🎉' : 'Aucune erreur trouvée pour ces filtres'}
        </div>
      )}

      {!loading && !err && filtered.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>N° mvt</th>
                  {mode === 'all' && <th style={th}>Opérateur</th>}
                  <th style={th}>Client</th>
                  <th style={th}>Typologie</th>
                  <th style={th}>Commentaire</th>
                  <th style={{ ...th, textAlign: 'right' }}>Coût</th>
                  <th style={{ ...th, textAlign: 'center' }}>Resp. BEL</th>
                  {(canEdit || canDelete) && <th style={{ ...th, textAlign: 'center' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => {
                  const cat = CATEGORY_COLOR[it.typology?.category] || { bg: '#f3f4f6', color: '#374151' };
                  const resp = RESP_BADGE[it.brainResponsibility] || RESP_BADGE.TBD;
                  return (
                    <tr key={it.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>{formatDate(it.date)}</td>
                      <td style={td}><code style={{ fontSize: 11, color: '#3b82f6' }}>{it.movementRef}</code></td>
                      {mode === 'all' && <td style={td}>{it.operator?.name || '—'}</td>}
                      <td style={td}>{it.project?.name || '—'}</td>
                      <td style={td}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, ...cat }}>
                          {it.typology?.label?.slice(0, 50) || '—'}
                        </span>
                      </td>
                      <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${it.explanation || ''}${it.depotComment ? '\n— Dépôt: ' + it.depotComment : ''}`}>
                        {it.explanation}
                        {it.reorderRef && <div style={{ fontSize: 10, color: '#94a3b8' }}>→ {it.reorderRef}</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: it.cost ? 600 : 400 }}>
                        {it.cost ? `${it.cost.toFixed(2)} €` : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: resp.bg, color: resp.color }}>{resp.label}</span>
                      </td>
                      {(canEdit || canDelete) && (
                        <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {canEdit && <button onClick={() => onEdit?.(it)} style={iconBtn} title="Modifier">✏️</button>}
                          {canDelete && <button onClick={() => handleDelete(it)} style={{ ...iconBtn, marginLeft: 4, background: '#fee2e2', color: '#b91c1c', borderColor: '#fecaca' }} title="Supprimer">🗑</button>}
                        </td>
                      )}
                    </tr>
                  );
                })}
                <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                  <td colSpan={mode === 'all' ? 6 : 5} style={{ ...td, textAlign: 'right' }}>TOTAL ({filtered.length} erreur{filtered.length > 1 ? 's' : ''}) :</td>
                  <td style={{ ...td, textAlign: 'right' }}>{totalCost.toFixed(2)} €</td>
                  <td colSpan={(canEdit || canDelete) ? 2 : 1}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' };
const td = { padding: '8px 10px' };
const inputStyle = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };
const primaryBtn = { padding: '7px 14px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const iconBtn = { padding: '3px 8px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 };
